/** angular */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { ALLOWED_LANGUAGES } from '../constants';
/** end angular */

/** constants */

/** end constants */

@Injectable({
  providedIn: 'root',
})
/**
 * Service used for using, saving and retrieving data relative to application language (localization).
 * For translation, pages still use directly the TranslateService.
 * It is injected inside app.module so it is always available inside the application
 */
export class DsLibLanguageService {
  /**
   * Constant allowed languages array
   */
  private ALLOWED_LANGUAGES: string[] = ALLOWED_LANGUAGES.map((language) => language.value);

  /**
   * Value of current language if it was changed manually by the user on the login page
   */
  private changedLanguage = '';

  /**
   * Language currently being used, added because translate.use() returns an observable.
   * Calling translate.currentLang before the observable has completed will not return the right language
   */
  private currentLang = '';

  /**
   * Creates an instance of DsLibLanguageService.
   *
   * @param translate TranslateService
   */
  constructor(private translate: TranslateService) {
    // Initialize translation
    this.translate.setDefaultLang(this.ALLOWED_LANGUAGES[0]);
    this.translate.addLangs(this.ALLOWED_LANGUAGES);
  }

  /**
   * Get latest lang having been given to the translateService to use as current language.
   * translate.currentLang can return a different value if translate.use() has not finished.
   *
   * @returns currentLang
   */
  public getCurrentLang(): string {
    return this.currentLang;
  }

  /**
   * Function called upon login, to check if the user
   * has changed the language manually on the login page.
   *
   * @returns changedLanguage
   */
  public getPickedLanguage(): string {
    return this.changedLanguage;
  }

  /**
   * Sets language picked by the user on the login page.
   *
   * @param language selected language
   */
  public setPickedLanguage(language: string) {
    this.changedLanguage = language;
  }

  /**
   * Sets language into localStorage.
   *
   * @param language selected language
   */
  public setStoredLanguage(language: string) {
    localStorage.setItem('lang', language);
  }

  /**
   * Returns string containing stored language (will be null if not set).
   *
   * @returns StoredLanguag
   */
  public getStoredLanguage(): string | null {
    return localStorage.getItem('lang');
  }

  /**
   * Use the last used language inside the app,
   * or use browser language if no previous language is found.
   */
  public useLoginLang(): void {
    let browserLanguage: string = this.translate.getBrowserLang();
    // language in localStorage takes over browser language settings.
    // When going to main page (after login), we will fetch the user's language and override these settings
    const localLanguage = this.getStoredLanguage();
    if (localLanguage) {
      browserLanguage = localLanguage;
    }
    this.use(browserLanguage, false);
  }

  /**
   * Sets translate service language and checks whether the language is supported or not,
   * otherwhise defaulted to 'en'.
   *
   * @param language Language to set
   * @param updateLocalStorage Whether to update localStorage when language has been changed
   */
  public use(language: string, updateLocalStorage = true) {
    if (!language) {
      return;
    }
    const allowedLanguage = this.ALLOWED_LANGUAGES.includes(language) ? language : this.ALLOWED_LANGUAGES[0];
    this.currentLang = allowedLanguage;
    /**
     * Subject might have to be used to change language of translateService, because of issue:
     * https://github.com/ngx-translate/core/issues/749
     * There is only a need for Subject if the language needs to be changed quickly (in a 100ms interval).
     * For now, this approach works
     */
    this.translate
      .use(allowedLanguage)
      .pipe(first())
      .subscribe(() => {
        if (!updateLocalStorage) {
          return;
        }
        /**
         * Update localStorage lang everytime it is changed (except when loading the login page)
         * to be able to retrieve the right language upon next login.
         */
        this.setStoredLanguage(allowedLanguage);
      });
  }
}
